<template>
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between">
				<WTitle text="Roles" />
				<PButton
					@click="openAdd()"
					icon="pi pi-plus"
					rounded
					size="small"
					v-tooltip.left="`Add a new role`"
				></PButton>
			</div>
		</template>
		<template #content>
			<PDataTable
				:alwaysShowPaginator="false"
				:loading="loading"
				:rowHover="true"
				:rows="100"
				:showGridlines="true"
				:stripedRows="true"
				:value="list"
				data-key="id"
				paginator
				size="small"
			>
				<template #empty>No roles found.</template>

				<PColumn field="name" header="Name" headerClass="py-3" sortable>
					<template #body="{ data }">
						<div class="flex align-items-center gap-2">
							<span>{{ data.name }}</span>
							<PTag v-if="data.is_system" severity="info">System</PTag>
						</div>
					</template>
				</PColumn>
				<PColumn bodyClass="text-wrap" field="description" header="Description" headerClass="py-3"></PColumn>
				<PColumn header="Permissions" headerClass="py-3" style="width: 130px;">
					<template #body="{ data }">
						<span>{{ (data.permissions || []).length }}</span>
					</template>
				</PColumn>
				<PColumn bodyClass="text-right" headerClass="py-3" style="width: 100px;">
					<template #body="{ data }">
						<PButton
							:disabled="data.is_system"
							@click="openEdit(data)"
							icon="pi pi-pencil"
							rounded
							severity="success"
							size="small"
							text
							v-tooltip.bottom="`Edit`"
						></PButton>
						<PButton
							:disabled="data.is_system"
							@click="doDelete($event, data.id)"
							icon="pi pi-trash"
							rounded
							severity="danger"
							size="small"
							text
							v-tooltip.bottom="`Delete`"
						></PButton>
					</template>
				</PColumn>
			</PDataTable>
		</template>
	</PCard>

	<!-- Add/Edit dialog -->
	<PDialog
		:header="editing ? 'Edit role' : 'Add a new role'"
		@hide="closeDialog()"
		modal
		style="width: 40rem; max-width: 95vw;"
		v-model:visible="showDialog"
	>
		<div class="flex flex-column gap-4">
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="name">Name</label>
				<PInputText
					autocomplete="off"
					id="name"
					size="small"
					v-model="item.name"
				></PInputText>
			</div>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="description">Description</label>
				<PInputText
					autocomplete="off"
					id="description"
					size="small"
					v-model="item.description"
				></PInputText>
			</div>

			<div class="flex flex-column gap-2">
				<label class="text-primary-50">Permissions</label>
				<div
					:key="group.component"
					class="border-1 surface-border border-round p-3 mb-2"
					v-for="group in groupedPermissions"
				>
					<div class="font-bold text-primary mb-2">{{ prettify(group.component) }}</div>
					<div class="flex flex-wrap gap-3">
						<div
							:key="perm.id"
							class="flex align-items-center gap-2"
							v-for="perm in group.permissions"
						>
							<PCheckbox
								:inputId="'perm-' + perm.id"
								:value="perm.id"
								v-model="item.permissions"
							></PCheckbox>
							<label :for="'perm-' + perm.id">{{ prettify(perm.action) }}</label>
						</div>
					</div>
				</div>
			</div>

			<div class="flex align-items-center justify-content-end gap-3">
				<PButton
					@click="editing ? saveUpdates() : doAdd()"
					label="Save"
					size="small"
				></PButton>
				<PButton
					@click="closeDialog()"
					label="Cancel"
					outlined
					severity="secondary"
					size="small"
				></PButton>
			</div>
		</div>
	</PDialog>

	<PConfirmDialog />
</template>

<script lang="ts">
	import { defineComponent } from 'vue'
	import BEBroker from '@/brokers/BEBroker'
	import Utils from '@/utils/Utils'
	import Constants from '@/helpers/Constants'

	export default defineComponent({
		name: 'RolesView',

		data() {
			return {
				component: 'roles',
				loading: true,
				showDialog: false,
				editing: false,
				list: [] as any[],
				permissions: [] as any[],
				item: {
					id: -1,
					name: '',
					description: '',
					permissions: [] as number[],
				},
			}
		},

		computed: {
			groupedPermissions(): any[] {
				const groups: Record<string, any[]> = {}
				this.permissions.forEach((perm: any) => {
					if (!groups[perm.component]) groups[perm.component] = []
					groups[perm.component].push(perm)
				})
				return Object.keys(groups).map((component) => ({
					component,
					permissions: groups[component],
				}))
			},
		},

		async mounted() {
			Utils.generatePid(Constants.OPID.READ)
			await this.loadPermissions()
			await this.refreshList()
			this.loading = false
		},

		methods: {
			prettify(value: string) {
				if (!value) return ''
				return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
			},

			async loadPermissions() {
				const res = await BEBroker.get('permissions')
				this.permissions = (res && res.length) ? res : []
			},

			async refreshList() {
				const res = await BEBroker.get(this.component)
				this.list = (res && res.length) ? res : []
			},

			openAdd() {
				this.editing = false
				this.item = { id: -1, name: '', description: '', permissions: [] }
				this.showDialog = true
			},

			openEdit(data: any) {
				this.editing = true
				this.item = {
					id: data.id,
					name: data.name,
					description: data.description || '',
					permissions: [...(data.permissions || [])],
				}
				this.showDialog = true
			},

			closeDialog() {
				this.showDialog = false
				this.editing = false
			},

			async doAdd() {
				if (this.item.name.trim() === '') {
					Utils.showErrorMessage('Role name is required.')
					return
				}
				this.loading = true
				Utils.generatePid(Constants.OPID.ADD)
				const payload = {
					name: this.item.name.trim(),
					description: this.item.description.trim(),
					permissions: this.item.permissions,
				}
				const res = await BEBroker.add(this.component, payload)
				if (res) {
					this.closeDialog()
					await this.refreshList()
					Utils.showSuccessMessage('Role has been added successfully.')
				}
				this.loading = false
			},

			async saveUpdates() {
				if (this.item.name.trim() === '') {
					Utils.showErrorMessage('Role name is required.')
					return
				}
				this.loading = true
				Utils.generatePid(Constants.OPID.UPDATE)
				const payload = {
					id: this.item.id,
					name: this.item.name.trim(),
					description: this.item.description.trim(),
					permissions: this.item.permissions,
				}
				const res = await BEBroker.update(this.component, payload)
				if (res) {
					this.closeDialog()
					await this.refreshList()
					Utils.showSuccessMessage('Role has been updated successfully.')
				}
				this.loading = false
			},

			async doDelete(event: any, id: number) {
				this.$confirm.require({
					target: event.currentTarget,
					header: 'Delete',
					message: 'Are you sure you want to delete this role?',
					icon: 'pi pi-info-circle',
					rejectClass: 'p-button-primary p-button-outlined p-button-sm',
					acceptClass: 'p-button-danger p-button-sm',
					rejectLabel: 'Cancel',
					acceptLabel: 'Delete',
					accept: async () => {
						this.loading = true
						Utils.generatePid(Constants.OPID.DELETE)
						await BEBroker.delete(this.component, id)
						await this.refreshList()
						this.loading = false
						Utils.showSuccessMessage('Role has been deleted successfully.')
					},
				})
			},
		},
	})
</script>
